import { useCallback, useState } from 'react'
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded'
import { AppDialog } from '../../../Common/AppDialog'
import { Pressable } from '../../../Common/Pressable'
import { TextInput } from '../../../Common/TextInput'
import { getGoogleSheetsPickerToken, parseSpreadsheetIdFromUrl } from '../../../../services/googleSheetsService'
import { STRINGS_NLS } from '../../../../nls/strings_nls'
import './gapi'

const PICKER_SCRIPT_ID = 'timelining-google-picker-api'
const pickerDeveloperKey = import.meta.env.VITE_GOOGLE_PICKER_API_KEY ?? ''

let pickerApiLoad: Promise<void> | null = null

const loadGooglePicker = () => {
  if (window.gapi?.picker) return Promise.resolve()
  if (pickerApiLoad) return pickerApiLoad
  pickerApiLoad = new Promise<void>((resolve, reject) => {
    const onApiLoaded = () => {
      window.gapi?.load('picker', {
        callback: resolve,
        onerror: () => reject(new Error('Google Picker could not load.')),
      })
    }
    const existing = document.getElementById(PICKER_SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', onApiLoaded, { once: true })
      existing.addEventListener('error', () => reject(new Error('Google Picker could not load.')), { once: true })
      return
    }
    const script = document.createElement('script')
    script.id = PICKER_SCRIPT_ID
    script.async = true
    script.src = 'https://apis.google.com/js/api.js'
    script.addEventListener('load', onApiLoaded, { once: true })
    script.addEventListener('error', () => reject(new Error('Google Picker could not load.')), { once: true })
    document.head.appendChild(script)
  }).catch((error) => {
    pickerApiLoad = null
    throw error
  })
  return pickerApiLoad
}

export type SelectedSheet = {
  id?: string
  name: string
  url?: string
}

export type GoogleSheetPickerButtonProps = {
  connectionId: string
  onSheetSelected: (sheet: SelectedSheet) => void | Promise<void>
  onError: (error: Error) => void
  label?: string
  className?: string
  developerKey?: string
  allowCreateNew?: boolean
}

export const GoogleSheetPickerButton = ({
  connectionId,
  onSheetSelected,
  onError,
  label = STRINGS_NLS.NLSGoogleSheetsExportButton.text,
  className = 'topbarPill topbarPill--sm',
  developerKey,
  allowCreateNew = true,
}: GoogleSheetPickerButtonProps) => {
  const [opening, setOpening] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [urlInput, setUrlInput] = useState('')

  const openPicker = useCallback(async () => {
    if (opening) return
    const activeDeveloperKey = developerKey ?? pickerDeveloperKey
    if (!activeDeveloperKey || activeDeveloperKey === '<add later>') {
      // Fall back directly to URL modal if Picker key is not configured
      setDialogOpen(true)
      return
    }

    setOpening(true)
    try {
      const [{ accessToken }] = await Promise.all([
        getGoogleSheetsPickerToken(connectionId),
        loadGooglePicker(),
      ])

      const googlePicker = window.gapi?.picker
      if (!googlePicker) throw new Error('Google Picker could not load.')

      const docsView = new googlePicker.DocsView(googlePicker.ViewId.DOCS)
      docsView.setMimeTypes('application/vnd.google-apps.spreadsheet')

      new googlePicker.PickerBuilder()
        .addView(docsView)
        .setOAuthToken(accessToken)
        .setDeveloperKey(activeDeveloperKey)
        .setCallback((data) => {
          if (data.action !== googlePicker.Action.PICKED) return
          const doc = data.docs?.[0]
          if (!doc?.id) return
          void Promise.resolve(
            onSheetSelected({
              id: doc.id,
              name: doc.name ?? 'Selected sheet',
              url: doc.url,
            }),
          ).catch((error) => onError(error instanceof Error ? error : new Error('Could not export to Google Sheets.')))
        })
        .build()
        .setVisible(true)
    } catch (error) {
      console.warn('[GoogleSheetPickerButton] Native picker failed, offering manual paste dialog:', error)
      setDialogOpen(true)
    } finally {
      setOpening(false)
    }
  }, [connectionId, developerKey, onError, onSheetSelected, opening])

  const handleConfirmUrl = () => {
    const parsedId = parseSpreadsheetIdFromUrl(urlInput)
    if (!parsedId) {
      onError(new Error('Invalid Google Sheet URL or ID.'))
      return
    }
    setDialogOpen(false)
    void Promise.resolve(
      onSheetSelected({
        id: parsedId,
        name: 'Linked Spreadsheet',
        url: urlInput.trim(),
      }),
    ).catch((err) => onError(err instanceof Error ? err : new Error('Could not export to Google Sheet.')))
  }

  const handleCreateNew = () => {
    setDialogOpen(false)
    void Promise.resolve(
      onSheetSelected({
        id: undefined,
        name: 'New Spreadsheet',
      }),
    ).catch((err) => onError(err instanceof Error ? err : new Error('Could not export to Google Sheet.')))
  }

  return (
    <>
      <Pressable
        className={className}
        disabled={opening}
        aria-label={label}
        onClick={(event) => {
          event.stopPropagation()
          void openPicker()
        }}
      >
        <TableChartRoundedIcon sx={{ fontSize: 15 }} />{' '}
        {opening ? STRINGS_NLS.NLSGoogleSheetsExporting.text : label}
      </Pressable>

      {dialogOpen && (
        <AppDialog
          open={dialogOpen}
          title={STRINGS_NLS.NLSGoogleSheetsExportDialogTitle.text}
          onDismiss={() => setDialogOpen(false)}
          actions={[
            ...(allowCreateNew
              ? [
                  {
                    label: STRINGS_NLS.NLSGoogleSheetsNewSheet.text,
                    onClick: handleCreateNew,
                    variant: 'secondary' as const,
                  },
                ]
              : []),
            {
              label: 'Cancel',
              onClick: () => setDialogOpen(false),
              variant: 'ghost' as const,
            },
            {
              label: 'Export to Sheet',
              onClick: handleConfirmUrl,
              variant: 'primary' as const,
              disabled: !parseSpreadsheetIdFromUrl(urlInput),
            },
          ]}
          message={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '320px' }}>
              <label style={{ fontSize: '13px', color: 'var(--muted)' }}>
                {STRINGS_NLS.NLSGoogleSheetsPasteUrlPrompt.text}
              </label>
              <TextInput
                value={urlInput}
                onChange={setUrlInput}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                aria-label={STRINGS_NLS.NLSGoogleSheetsPasteUrlPrompt.text}
                autoFocus
              />
            </div>
          }
        />
      )}
    </>
  )
}
